import { shadow, zIndices } from "@obs/tokens/index.stylex.ts";
import type { StyleXStyles } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useEffect, useRef, useState } from "react";

export type StickyProps = {
	children: ReactNode;
	position?: "top" | "bottom";
	sx?: StyleXStyles;
};

const styles = stylex.create({
	sticky: {
		insetInline: 0,
		position: "sticky",
		zIndex: zIndices.sticky,
	},
	top: {
		top: 0,
	},
	bottom: {
		bottom: 0,
	},
	stickyTopShadow: {
		boxShadow: shadow.neutralStickyTop,
	},
	stickyBottomShadow: {
		boxShadow: shadow.neutralStickyBottom,
	},
	sentinel: {
		blockSize: "1px",
	},
});

export const Sticky = ({ children, position = "bottom", sx }: StickyProps) => {
	const stickyRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const [isSticky, setIsSticky] = useState(false);

	useEffect(() => {
		const sentinelElement = sentinelRef.current;
		const stickyElement = stickyRef.current;
		if (!sentinelElement || !stickyElement) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					setIsSticky(!entry.isIntersecting);
				}
			},
			{
				rootMargin:
					position === "bottom"
						? `0px 0px -${stickyElement.offsetHeight}px 0px`
						: `-${stickyElement.offsetHeight}px 0px 0px 0px`,
				threshold: 0,
			},
		);

		observer.observe(sentinelElement);

		return () => {
			observer.disconnect();
		};
	}, [position]);

	const shadowStyle = isSticky && (position === "bottom" ? styles.stickyBottomShadow : styles.stickyTopShadow);

	return (
		<>
			{position === "bottom" && <div ref={sentinelRef} {...stylex.props(styles.sentinel)} />}
			<div
				ref={stickyRef}
				{...stylex.props(styles.sticky, position === "bottom" ? styles.bottom : styles.top, shadowStyle, sx)}
			>
				{children}
			</div>
			{position === "top" && <div ref={sentinelRef} {...stylex.props(styles.sentinel)} />}
		</>
	);
};
